/**
 * Candidate sign-in — email OTP.
 *
 * *** THE NEUTRAL RESPONSE ***
 * `POST /api/auth/otp/request` answers identically whether or not an account exists — one shape,
 * one constant value. This screen is where that guarantee can be thrown away, so it doesn't:
 *   - After a request, the copy says "if that address can receive a code" — never "we've sent you a
 *     code", which would assert the address exists.
 *   - The step advances to "enter your code" REGARDLESS of whether the address is known. A stranger
 *     typing an address gets the same screen a real candidate does, and learns nothing.
 *   - Verify failures are coarse: `invalid_code` covers a wrong code AND an unknown/deactivated
 *     account (the service folds them together on purpose); un-folding them here would rebuild the
 *     oracle the API collapsed.
 *
 * First sign-in CREATES the account (verify returns a `userId`). react-hook-form + zod validate the
 * shapes client-side before the round-trip; the server is the real gate.
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient, CandidateApiError } from '@/lib/api-client';
import { untilPhrase } from '@/lib/timeline';
import { useCandidateSession } from '@/auth/session';
import { ThreadMark } from '@/components/ThreadMark';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/field';

type Step = 'email' | 'code';

/** How long the "send a new code" affordance stays disabled after a resend (mirrors mobile's 30s). */
const RESEND_CODE_COOLDOWN_S = 30;

/**
 * Map an `otp/request` failure (shared by the first send and the resend) to a banner message.
 * `invalid_email` is handled on the form itself, so it never reaches here. The captcha branch is
 * honest-by-construction: the day `Providers:Captcha=Real` flips before a web widget lands, the
 * request would 400 `captcha_required` / `captcha_failed`, and without this branch that would read as
 * a generic outage — so it gets its own, truthful line.
 */
function requestBanner(code: string | undefined, retryAfterSeconds: number | undefined): string {
  if (code === 'captcha_required' || code === 'captcha_failed') {
    return 'We couldn’t confirm you’re human just now. Refresh the page and try again.';
  }
  if (code === 'rate_limited') {
    return retryAfterSeconds !== undefined
      ? `Too many requests. Try again in ${untilPhrase(new Date(Date.now() + retryAfterSeconds * 1000))}.`
      : 'Too many requests. Wait a moment and try again.';
  }
  return 'Couldn’t send a code just now. Try again in a moment.';
}

const emailSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('That doesn’t look like an email address.'),
});
const codeSchema = z.object({
  code: z.string().trim().min(1, 'Enter the code we sent.'),
});
type EmailValues = z.infer<typeof emailSchema>;
type CodeValues = z.infer<typeof codeSchema>;

/** Verify-step error copy. Coarse by design — see the module comment. */
function verifyErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'code_expired':
      return 'That code has expired. Ask for a new one.';
    case 'too_many_attempts':
    case 'rate_limited':
      return 'Too many tries. Wait a moment and try again.';
    default:
      // invalid_code and everything else — says nothing about whether the address is known.
      return 'That code didn’t work. Check it and try again.';
  }
}

const MailIcon = (
  <svg viewBox="0 0 24 24" fill="none" width={17} height={17} aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

export function SignIn() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [banner, setBanner] = useState<string | null>(null);
  // The "send a new code" resend: a 30s cooldown countdown + a live-region confirmation, so a
  // candidate whose first code never arrived can ask for another without leaving the step.
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendConfirmed, setResendConfirmed] = useState(false);

  const signIn = useCandidateSession((state) => state.signIn);
  const accessToken = useCandidateSession((state) => state.accessToken);
  const location = useLocation();
  const navigate = useNavigate();

  const emailForm = useForm<EmailValues>({ resolver: zodResolver(emailSchema), defaultValues: { email: '' } });
  const codeForm = useForm<CodeValues>({ resolver: zodResolver(codeSchema), defaultValues: { code: '' } });

  // Move focus to the code field when the step advances — the keyboard path shouldn't hunt.
  // `setFocus` (not a manual ref) so RHF's own `register` ref isn't overwritten.
  const { setFocus: setCodeFocus } = codeForm;
  useEffect(() => {
    if (step === 'code') setCodeFocus('code');
  }, [step, setCodeFocus]);

  // Tick the resend cooldown down to zero (one timer per second, cleaned up between ticks).
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const from = (location.state as { from?: string } | null)?.from;
  if (accessToken !== null) {
    return <Navigate to={from ?? '/'} replace />;
  }

  async function onRequest(values: EmailValues) {
    setBanner(null);
    setResendConfirmed(false);
    setResendCooldown(0);
    try {
      await apiClient.requestOtp(values.email);
      setEmail(values.email.trim());
      // Advance unconditionally — the response is neutral and must not be branched on.
      setStep('code');
    } catch (caught) {
      const err = caught instanceof CandidateApiError ? caught : undefined;
      if (err?.code === 'invalid_email') {
        emailForm.setError('email', { message: 'That doesn’t look like an email address.' });
      } else {
        setBanner(requestBanner(err?.code, err?.retryAfterSeconds));
      }
    }
  }

  /** Re-send the sign-in code from the code step (same neutral request), then start the cooldown. */
  async function onResendCode() {
    setBanner(null);
    setResendConfirmed(false);
    try {
      await apiClient.requestOtp(email);
      setResendConfirmed(true);
      setResendCooldown(RESEND_CODE_COOLDOWN_S);
    } catch (caught) {
      const err = caught instanceof CandidateApiError ? caught : undefined;
      setBanner(requestBanner(err?.code, err?.retryAfterSeconds));
    }
  }

  async function onVerify(values: CodeValues) {
    setBanner(null);
    try {
      const tokens = await apiClient.verifyOtp(email, values.code);
      signIn(tokens);
      navigate(from ?? '/', { replace: true });
    } catch (caught) {
      const code = caught instanceof CandidateApiError ? caught.code : undefined;
      codeForm.setError('code', { message: verifyErrorMessage(code) });
    }
  }

  return (
    <main className="flex min-h-svh flex-col items-center justify-center px-5 py-10">
      <div className="shell-mount w-full max-w-[400px]">
        <div className="mb-6 flex flex-col items-center text-center">
          <ThreadMark className="h-6 w-auto" />
          <h1 className="mt-4 font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">
            Sign in to By My Word
          </h1>
          <p className="mt-1.5 max-w-[34ch] font-body text-[13px] leading-relaxed text-ink-quiet">
            We’ll email you a one-time code — no password to remember.
          </p>
        </div>

        <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
          {banner && (
            <p role="alert" className="mb-3 rounded-md bg-coral-tint px-3 py-2 font-body text-[12.5px] text-coral-text">
              {banner}
            </p>
          )}

          {step === 'email' ? (
            <form onSubmit={emailForm.handleSubmit(onRequest)} noValidate>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                inputMode="email"
                icon={MailIcon}
                error={emailForm.formState.errors.email?.message}
                {...emailForm.register('email')}
              />
              <div className="mt-4">
                <Button type="submit" size="block" disabled={emailForm.formState.isSubmitting}>
                  {emailForm.formState.isSubmitting ? 'Sending…' : 'Email me a code'}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={codeForm.handleSubmit(onVerify)} noValidate>
              {/* Neutral by construction — never "we sent you a code" (that asserts existence). */}
              <p role="status" className="mb-1 font-body text-[12.5px] leading-relaxed text-ink-quiet">
                If that address can receive a code, one’s on its way. Enter it below.
              </p>
              <TextField
                label="Sign-in code"
                inputMode="numeric"
                autoComplete="one-time-code"
                error={codeForm.formState.errors.code?.message}
                {...codeForm.register('code')}
              />
              <div className="mt-4 flex flex-col gap-2">
                <Button type="submit" size="block" disabled={codeForm.formState.isSubmitting}>
                  {codeForm.formState.isSubmitting ? 'Checking…' : 'Sign in'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="block"
                  onClick={() => {
                    setStep('email');
                    codeForm.reset();
                    setBanner(null);
                    setResendConfirmed(false);
                    setResendCooldown(0);
                  }}
                >
                  Use a different email
                </Button>
              </div>

              <div className="mt-3 flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={onResendCode}
                  disabled={resendCooldown > 0}
                  className="rounded-md px-2 py-1 font-body text-[12.5px] font-medium text-sage-text underline-offset-2 transition-colors duration-ui ease-thread hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 disabled:text-ink-quiet disabled:no-underline"
                >
                  {resendCooldown > 0 ? `Send a new code (${resendCooldown}s)` : 'Send a new code'}
                </button>
                {/* Live region kept mounted so the confirmation is announced when it appears. */}
                <p aria-live="polite" className="min-h-[1rem] font-body text-[11.5px] text-ink-quiet">
                  {resendConfirmed && resendCooldown > 0 ? 'A new code is on its way.' : ''}
                </p>
              </div>
            </form>
          )}
        </div>

        <p className="mt-4 text-center font-body text-[11px] leading-relaxed text-ink-quiet">
          We contact your referees — never you. We only use their details for this reference.
        </p>
      </div>
    </main>
  );
}
